import React from 'react';
import { ArrowLeft, ChevronRight, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 11: PRESTIGE - Luxury Fashion, Elegant (Inspired by Shopify's Prestige theme)
const Template11 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-black min-h-screen text-white font-serif">
            {/* Elegant Navigation */}
            <nav className="border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex justify-between items-center">
                        <button
                            onClick={() => navigate('/TemplateThemes')}
                            className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
                        >
                            <ArrowLeft size={16} /> Back
                        </button>

                        <h1 className="text-2xl tracking-widest font-light">PRESTIGE</h1>

                        <button className="text-sm text-white/70 hover:text-white transition-colors">
                            Menu
                        </button>
                    </div>
                </div>
            </nav>

            {/* Full-Screen Hero with Parallax Effect */}
            <section className="relative h-screen">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1509631179647-0177331693ae?w=1920&h=1080&fit=crop"
                        alt="Luxury Fashion"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                </div>
                <div className="relative h-full flex items-end">
                    <div className="max-w-7xl mx-auto px-6 pb-20 w-full">
                        <div className="max-w-xl">
                            <p className="text-sm tracking-widest mb-4 text-white/70">SPRING COLLECTION 2025</p>
                            <h2 className="text-6xl md:text-7xl font-light mb-6 leading-tight">
                                Timeless<br />Elegance
                            </h2>
                            <p className="text-lg text-white/80 mb-8 font-sans">
                                Discover pieces that define sophistication
                            </p>
                            <button className="group flex items-center gap-3 text-white border-b border-white pb-2 hover:gap-5 transition-all">
                                <span className="text-sm tracking-wider">EXPLORE COLLECTION</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dual Featured Products */}
            <section className="max-w-7xl mx-auto px-6 py-24">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="group cursor-pointer">
                        <div className="relative overflow-hidden aspect-[3/4] mb-6">
                            <img
                                src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&h=1000&fit=crop"
                                alt="Women's Collection"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                        <div className="text-center">
                            <h3 className="text-3xl font-light mb-2">Women's</h3>
                            <p className="text-white/60 mb-4">Curated luxury</p>
                            <button className="text-sm tracking-wider border-b border-white/30 hover:border-white transition-colors pb-1">
                                SHOP NOW
                            </button>
                        </div>
                    </div>

                    <div className="group cursor-pointer">
                        <div className="relative overflow-hidden aspect-[3/4] mb-6">
                            <img
                                src="https://images.unsplash.com/photo-1488161628813-04466f872be2?w=800&h=1000&fit=crop"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                alt="Men's Collection"
                            />
                        </div>
                        <div className="text-center">
                            <h3 className="text-3xl font-light mb-2">Men's</h3>
                            <p className="text-white/60 mb-4">Refined essentials</p>
                            <button className="text-sm tracking-wider border-b border-white/30 hover:border-white transition-colors pb-1">
                                SHOP NOW
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Best Sellers */}
            <section className="bg-zinc-900 py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <p className="text-sm tracking-widest text-white/60 mb-4">CUSTOMER FAVORITES</p>
                        <h2 className="text-4xl font-light">Best Sellers</h2>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[1, 2, 3, 4].map((id) => (
                            <div key={id} className="group">
                                <div className="relative overflow-hidden aspect-square mb-4 bg-zinc-800">
                                    <img
                                        src={`https://images.unsplash.com/photo-${1540518614846 + id * 50000}-e0aa7f38f5e9?w=600&h=600&fit=crop`}
                                        alt={`Product ${id}`}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                    <div className="absolute top-4 right-4 flex gap-1">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} size={12} fill="white" className="text-white" />
                                        ))}
                                    </div>
                                </div>
                                <h3 className="text-sm font-light mb-1 text-center">Luxury Item {id}</h3>
                                <p className="text-sm text-white/60 text-center">$XXX.00</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Full-Width Statement */}
            <section className="relative h-[500px]">
                <img
                    src="https://images.unsplash.com/photo-1483118714900-540cf339fd46?w=1920&h=800&fit=crop"
                    alt="Statement"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="text-center px-6">
                        <h2 className="text-5xl md:text-6xl font-light mb-6">Crafted for Perfection</h2>
                        <p className="text-xl text-white/80 max-w-2xl mx-auto">
                            Every piece tells a story of meticulous craftsmanship and timeless design
                        </p>
                    </div>
                </div>
            </section>

            {/* Categories Grid */}
            <section className="max-w-7xl mx-auto px-6 py-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {['Dresses', 'Accessories', 'Footwear'].map((category, index) => (
                        <div key={index} className="group relative overflow-hidden aspect-[4/5] cursor-pointer">
                            <img
                                src={`https://images.unsplash.com/photo-${1495555961986 + index * 30000}-5e4fcdf4b05f?w=600&h=800&fit=crop`}
                                alt={category}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                    <h3 className="text-3xl font-light mb-4">{category}</h3>
                                    <span className="text-sm tracking-wider border-b border-white pb-1">
                                        DISCOVER
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/10 py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
                        <div>
                            <h3 className="text-lg tracking-wider mb-6">PRESTIGE</h3>
                            <p className="text-sm text-white/60 leading-relaxed">
                                Redefining luxury fashion with timeless elegance and exceptional quality.
                            </p>
                        </div>
                        <div>
                            <h4 className="text-sm tracking-wider mb-4 text-white/80">SHOP</h4>
                            <ul className="space-y-3 text-sm text-white/60">
                                <li><a href="#" className="hover:text-white transition-colors">New Arrivals</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Collections</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Sale</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-sm tracking-wider mb-4 text-white/80">SUPPORT</h4>
                            <ul className="space-y-3 text-sm text-white/60">
                                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Shipping</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Returns</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-sm tracking-wider mb-4 text-white/80">CONNECT</h4>
                            <ul className="space-y-3 text-sm text-white/60">
                                <li><a href="#" className="hover:text-white transition-colors">Instagram</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Facebook</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Pinterest</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-white/10 pt-8 text-center">
                        <p className="text-sm text-white/40">&copy; 2025 Prestige. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template11;
